import { imageFromEnv } from "@p0security/integrations-gcloud-shared/mcp/servers";
import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { GoogleDriveAiIconUrl } from "./icon.ts";
import { googleDriveAiMcpTools } from "./mcp-tools.ts";
import type { GoogleDriveAiIntegration } from "./types.ts";

// The image already retries a 404 for 30s while a new ACL entry propagates; the
// rest is the minted service account's impersonation binding.
const googleDriveAiPropagationHint = propagationHint({
  resource: "Google Drive",
  normalWait: "15 to 45 seconds",
  maxWait: "2 minutes",
  pollInterval: "10 seconds",
});

const prefixArgs = ["google-drive-ai", "resource"];

const accessPrompt = `Request access to a folder or file in a Google shared drive by calling with argv:

  [<drive>, <resource>, <role>, "--reason", <reason>]

where:

  - drive: The shared drive's identifier in P0, which is not its Google Drive
    ID. Use the list tool to enumerate them.
  - resource: The ID of the folder or file, as in its Drive URL
    (e.g. /folders/<id>). Use the list tool, passing the drive, to enumerate
    them. Access to a folder covers everything in it.
  - role: 'reader' to read, or 'writer' to also create and edit
  - reason: The reason access is needed

Every tool is listed whether or not your access covers an item; Drive answers
404 for an item your access does not reach.
`;

/** The P0-built `gdrive` image over the Drive v3 REST API.
 *
 * A container rather than Google's hosted Drive MCP service, which refuses
 * every tool call for any credential P0 can present. The token is for the
 * service account P0 minted for the grant and shared the item with, not for the
 * calling session — a Drive ACL names an address, so there is no federated
 * principal Drive would recognize.
 */
export const googleDriveAiMcp: McpServerSpec & { type: "container" } = {
  type: "container",
  label: "Google Drive",
  iconUrl: GoogleDriveAiIconUrl,
  credential: {
    type: "impersonation",
    csp: "gcp",
    // Drive's own scope, not `cloud-platform`: the gateway refuses a scope
    // spanning every API the identity can reach.
    scopes: ["https://www.googleapis.com/auth/drive"],
  },
  // The Drive ACL entry P0 wrote decides what the token reaches.
  authorization: "upstream",
  transport: "stdio",
  tools: { type: "manual", tools: googleDriveAiMcpTools },
  container: {
    command: [],
    args: [],
    envs: {
      GCP_BEARER_TOKEN: "{{credential.token}}",
    },
    image: {
      image: imageFromEnv({
        env: "GDRIVE_IMAGE",
        fallback: "p0security/gdrive",
      }),
    },
  },
  access: {
    prompt: accessPrompt,
    prefixArgs,
    propagationHint: googleDriveAiPropagationHint,
  },
  list: {
    fields: {
      drive: {
        description: "Google shared drive, by its P0 identifier",
        required: [],
      },
      resource: {
        description: "Folder or file in a shared drive, by ID",
        required: ["drive"],
      },
      role: {
        description: "Drive role to grant on the folder or file",
        required: ["drive"],
      },
    },
    prefixArgs,
  },
  location: {
    key: "drive",
    description: "Google shared drive, by its P0 identifier",
  },
};

/** Whether {@link googleDriveAiMcp} can be added: only once a shared drive is
 * installed, since without one there is nothing an agent could request. */
export const googleDriveAiMcpServerAvailability = {
  googleDrive: (installed: Record<string, NewConfig>): boolean =>
    installedIds(
      "shared-drive",
      (installed["google-drive-ai"] ?? {}) as GoogleDriveAiIntegration
    ).length > 0,
};
